import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { parse } from 'query-string';

import PasswordReset, {
  ResetState,
  ResetType
} from '../components/PasswordReset';
import getAuthModule from '../auth';
import history from '../history';
import { getAuthData } from '../reducers';
import {
  AuthData,
  AuthError,
  ForgotPasswordResponse,
  StoreState,
  LoginExceptions
} from '../types';
import * as built from '../built';
import {
  handleNotAuthorizedException,
  handleLimitExceededException,
  showToast,
  ToastOpts,
  trackSnowplowEvent,
  SnowplowOpts
} from '../actions';
import { getLanguageReady } from 'src/reducers/i18n';
import l10n from '@built/i18n';

const authModule = getAuthModule();

export class PasswordResetContainer extends React.Component<
  PasswordResetContainerProps,
  PasswordResetContainerState
> {
  constructor(props: PasswordResetContainerProps) {
    super(props);
    const routeState = this.getRouteState(props);

    // if we have a user provided email (via login or query string)
    // use it so we can prepopulate the password reset email field
    const email =
      (this.props.authData && this.props.authData.userProvidedEmail) ||
      routeState.email ||
      '';
    this.state = {
      ...routeState,
      email,
      emailValid: false,
      password: '',
      showEmailError: false
    };
  }

  componentWillReceiveProps(newProps: PasswordResetContainerProps) {
    const { location: newLocation } = newProps;
    const { location: oldLocation } = this.props;
    if (newLocation.search !== oldLocation.search) {
      this.setState(this.getRouteState(newProps));
    }
  }

  render() {
    const {
      code,
      resetState,
      resetType,
      email,
      emailValid,
      error,
      password,
      phoneNumber,
      showEmailError
    } = this.state;
    const { className } = this.props;
    return (
      <PasswordReset
        phoneNumber={phoneNumber}
        className={className}
        code={code}
        email={email}
        emailValid={emailValid}
        resetState={resetState}
        resetType={resetType}
        error={error}
        login={this.login}
        password={password}
        onChange={this.handleChange}
        onEmailAddressChange={this.handleEmailAddressChange}
        gotoReset={this.gotoReset}
        requestNewCode={this.requestNewCode}
        requestReset={this.requestReset}
        resetPassword={this.resetPassword}
        showEmailError={showEmailError}
      />
    );
  }

  /**
   * If the user is specified in the URL then they should be shown the UI
   * to reset their password as they've already been provided and entered
   * a code. Otherwise they need to provide their info to request a reset
   * or provide their code.
   */
  getRouteState(props: PasswordResetContainerProps) {
    const { user, code = '' } = parse(props.location.search);
    const resetState =
      user && code ? ResetState.RESETTING : ResetState.REQUESTING;
    return { code, email: user, resetState };
  }

  login = () => {
    history.push('/');
  };

  /**
   * Redirects the user to the reset page with their email and code as query
   * params. Then the password reset field will be displayed and the code and
   * email will be captured from the URL, just as they would be if the user was
   * coming from a password reset link.
   */
  gotoReset = (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    // since we use the URL to push state, we need to encode the email
    // address as it can contain characters that the browser will auto
    // translate (e.g, plus signs to spaces) -- LOGIN-108
    history.push(
      `/reset?user=${encodeURIComponent(this.state.email)}&code=${
        this.state.code
      }`
    );
  };

  /**
   * Performs the actual resetting of the user's password by sending
   * in the verification code recieved via email or SMS and the
   * new password they provided.
   */
  resetPassword = (event: React.SyntheticEvent<HTMLElement>) => {
    event.preventDefault();
    const { code, email, password } = this.state;

    // LOGIN-214 don't let the form submit without a code
    if (!code) {
      this.setState({
        resetState: ResetState.RESET_ERRORED,
        error: {
          message: l10n.t(
            'Auth:Mfa.errors.missingVerificationCode',
            'Please provide a verification code.'
          )
        }
      });
      return;
    }

    authModule.resetPassword(email!, code!, password!).then(
      () => {
        this.setState({ resetState: ResetState.RESET_ACCEPTED });
      },
      error => {
        if (error.name === LoginExceptions.CodeMismatchException) {
          this.setState({
            resetState: ResetState.RESET_ERRORED,
            error: {
              message: l10n.t(
                'Auth:Mfa.errors.invalidCodeError',
                'Please check the code you entered and try again.'
              )
            }
          });
        } else {
          this.handleError(error);
        }
      }
    );
    this.setState({ resetState: ResetState.RESET_SUBMITTED });
  };

  /**
   * Requests a new SMS code to be sent out which is basically
   * requesting the forgot password flow to be restarted. This
   * handles this special case.
   */
  requestNewCode = (event: React.SyntheticEvent<HTMLElement>) => {
    const { email, phoneNumber } = this.state;
    const last4 = phoneNumber!.slice(phoneNumber!.length - 4);
    if (!email) {
      return;
    }

    authModule
      .forgotPassword(email)
      .then((result: ForgotPasswordResponse) => {
        // for this special case, it must be SMS delivery
        if (result.CodeDeliveryDetails.DeliveryMedium !== 'SMS') {
          this.handleError(new Error('Invalid delivery medium.'));
          return;
        }

        this.props.showToast(
          l10n.t('SetupMfa:toaster.resendVerificationCodeMessage', {
            defaultValue:
              'A new verification code was sent to the number ending in {{last4}}.',
            last4
          })
        );
      })
      .catch((error: Error) => {
        this.handleError(error);
      });
  };

  /**
   * Request a reset and parse the response to determine
   * how the reset info was sent.
   */
  requestReset = (
    event: React.SyntheticEvent<HTMLElement>,
    allowExpired: boolean = true
  ) => {
    event.preventDefault();
    const snowplowOpts = {
      eventCategory: 'login',
      eventAction: 'send-reset-link',
      eventId: null,
      eventContext: 'forgot-password-page'
    };
    trackSnowplowEvent(snowplowOpts);

    if (!this.validateSubmitRequest()) {
      return;
    }
    const { email } = this.state;

    // user only provided an email address, so kick off the forgot
    // password flow
    this.setState({
      resetState: ResetState.REQUEST_SUBMITTED
    });

    authModule
      .forgotPassword(this.state.email)
      .then((result: ForgotPasswordResponse) => {
        const newState = {
          phoneNumber: '',
          resetState: ResetState.REQUEST_ACCEPTED,
          resetType: ResetType.EMAIL
        };
        if (result.CodeDeliveryDetails.DeliveryMedium === 'SMS') {
          // if the code was delivered via SMS, then set the state
          // such that the user is immediately taken to the reset
          // password form and includes the SMS code entry
          newState.phoneNumber = result.CodeDeliveryDetails.Destination;
          newState.resetState = ResetState.RESETTING;
          newState.resetType = ResetType.PHONE_NUMBER;
        }
        this.setState(newState);
      })
      .catch((error: Error) => {
        // if cognito forgot password failed, check to see if the
        // reason is because the user didn't exist, and if so,
        // attempt to migrate the user and start the reset password
        // flow via BAPI
        if (error.name === LoginExceptions.UserNotFoundException) {
          const newState = {
            resetState: ResetState.REQUEST_ACCEPTED,
            resetType: ResetType.EMAIL
          };
          built
            .forgotPassword(email)
            .then((result: boolean) => {
              this.setState(newState);
            })
            .catch((builtError: Error) => {
              this.setState(newState);
            });
        } else if (error.name === LoginExceptions.NotAuthorizedException) {
          // Initial run, allow Cognito expire user
          if (allowExpired) {
            // Attempt to remove expiration due to incomplete migration flow
            built
              .unexpire(email)
              .then((success: boolean) => {
                if (!success) {
                  // Wasn't successful, continue with original flow
                  this.handleError(error);
                } else {
                  // We need to keep the original event viable in order to do a
                  // recursive call here.
                  event.persist();

                  // Requeue the reset request
                  this.requestReset(event, false);
                }
              })
              .catch((err: Error) => {
                this.handleError(err);
              });
          } else {
            this.handleError(error);
          }
        } else {
          this.handleError(error);
        }
      });
  };

  handleError = (error: Error) => {
    if (error.name === LoginExceptions.LimitExceededException) {
      this.setState({
        resetState: ResetState.REQUEST_ERRORED,
        error: handleLimitExceededException(error)
      });
    } else {
      this.setState({
        resetState: ResetState.REQUEST_ERRORED,
        error: handleNotAuthorizedException(error)
      });
    }
  };

  handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const property = event.currentTarget.name;
    const { error } = this.state;

    this.setState({
      ...this.state,
      [property]: event.currentTarget.value,
      // LOGIN-214 reset validation errors on verification code change
      error: property === 'code' && error ? undefined : error
    });
  };

  /**
   * EmailField handler that receives an email value and boolean
   * indicating if the email address is considered valid.
   */
  handleEmailAddressChange = (email: string, emailValid: boolean) => {
    this.setState({
      ...this.state,
      email,
      emailValid,
      showEmailError: false
    });
  };

  /**
   * Validates that a valid email was provided before allowing
   * the form to submit
   */
  validateSubmitRequest = () => {
    let showEmailError = false;
    if (!this.state.email || !this.state.emailValid) {
      showEmailError = true;
    }

    this.setState({ ...this.state, showEmailError });
    return !showEmailError;
  };
}

// Hook up the password reset flow to the router to retrieve the current location
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(PasswordResetContainer));

export interface PasswordResetContainerProps extends RouteComponentProps<{}> {
  authData: AuthData;

  /** Optional class name to override styles */
  className?: string;

  /** Flag to determine if translations are ready and trigger render */
  languageReady: boolean;

  /** Callback to show a toast */
  showToast(message: string, opts?: ToastOpts): void;

  /** Callback to track an event to snowplow */
  trackSnowplowEvent(opts: SnowplowOpts): void;
}

export interface PasswordResetContainerState {
  /** The current password reset code */
  code: string;

  /** The email of the user resetting their password */
  email: string;

  /** Is the email value a valid email address? */
  emailValid: boolean;

  /** The new password */
  password: string;

  /** Optionally provide a phone number to send the reset code to */
  phoneNumber?: string;

  /** Whether the current state of the form is valid or should display an error */
  isValid?: boolean;

  /** The current state in the password reset flow */
  resetState: ResetState;

  /** The type of the reset we're doing: email- or phone-based */
  resetType?: ResetType;

  /** An error to display to the user */
  error?: AuthError | Error;

  /** Whether to show inline validation error on the email field */
  showEmailError: boolean;
}

function mapStateToProps(
  state: StoreState,
  _ownProps?: Pick<PasswordResetContainerProps, 'className'>
): Pick<PasswordResetContainerProps, 'authData' | 'languageReady'> {
  return {
    authData: getAuthData(state),
    languageReady: getLanguageReady(state)
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<PasswordResetContainerProps, 'showToast' | 'trackSnowplowEvent'> {
  return {
    showToast(message: string, opts?: ToastOpts) {
      return dispatch(showToast(message, opts));
    },
    trackSnowplowEvent(opts: SnowplowOpts) {
      return dispatch(trackSnowplowEvent(opts));
    }
  };
}



// WEBPACK FOOTER //
// ./src/containers/PasswordReset.tsx